/**
 * Application Entry Point
 * 
 * This is the main entry point for the restructured frontend application.
 * It initializes the application with all necessary providers and routing.
 * 
 * Requirements: 1.1, 1.2, 1.3, 2.2, 3.1, 3.2, 3.3, 3.4, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 5.1, 5.2, 5.3, 5.4, 6.1, 6.2
 */

import { useState, useEffect, useCallback, useContext, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Theme CSS imports
import './styles/globals.css';
import './styles/theme.css';

// Store and Providers
import store from './store/store';
import { ThemeProvider } from './app/providers/ThemeProvider';
import { NotificationSummaryProvider } from './shared/notifications/NotificationSummaryProvider';

// Components
import { Navbar } from './shared/components/layout';
import { ImagoSidebar } from './shared/components/layout/ImagoSidebar';
import SandboxWelcomeModal from './features/auth/components/SandboxWelcomeModal';

// Routes
import { routes } from './app/routes';


// Constants
import { ROUTES, STORAGE_KEYS } from './shared/utils/constants';
import { fetchUserRoles } from './store/slices/rolesSlice';

// Legacy components (relative imports from src/)
import { GlobalProvider } from './app/providers/GlobalProvider';
import { GlobalContext } from './app/providers/GlobalProvider';
import { EnvironmentsProvider } from './app/providers/EnvironmentsProvider';
import { TestPlansProvider } from './features/test-plans/context/TestPlansProvider';
import GlobalLoader from './shared/components/ui/GlobalLoader';
import Walkthrough from './shared/components/Walkthrough';

import LandingPage from './pages/LandingPage/LandingPage.jsx';

// Chatbot components and hooks
// Imported from their own modules rather than the feature barrel. The barrel
// re-exports ChatMessageList too, and a module that is both statically and
// dynamically imported stays in the static graph — so pulling anything through
// the barrel dragged react-syntax-highlighter back into the entry chunk and
// silently defeated the lazy() below.
import { ChatbotFAB } from './features/chatbot/components/ChatbotFAB';
import { ChatbotPanel } from './features/chatbot/components/ChatbotPanel';
import { ChatSessionSidebar } from './features/chatbot/components/ChatSessionSidebar';
import { ChatInput } from './features/chatbot/components/ChatInput';
import { useChatbot } from './features/chatbot/hooks/useChatbot';
import { useAgentPreference } from './features/chatbot/hooks/useAgentPreference';
import { useChatSessions } from './features/chatbot/hooks/useChatSessions';
import { useChatContext } from './features/chatbot/hooks/useChatContext';
import { usePinsAndBookmarks } from './features/chatbot/hooks/usePinsAndBookmarks';
import { submitFeedback, getCommands, uploadFile } from './features/chatbot/services/chatbotApi';
// The AI generation slash commands (/generate-test, /generate-body, …). The
// chat input already owns the '/' key via SlashCommandDropdown, so these are
// merged into that ONE list rather than mounted as a second competing menu.
import { AI_GENERATION_COMMANDS } from './features/ai-generation/components/SlashCommandMenu';
import { useSandboxMode } from './features/auth/hooks/useSandboxMode';
import { useOrchestrationChatbotRouting } from './features/chatbot/hooks/useOrchestrationChatbotRouting';
import { OrchestrationChatPreview } from './features/chatbot/components/OrchestrationChatPreview';
import { readToken } from './shared/auth/session';
// The two message surfaces both render markdown through react-syntax-highlighter,
// which is ~617 kB. Loading it eagerly meant every visitor paid for it — landing
// page included — even though the chat only mounts once authenticated and these
// render only once the panel is open. Deferred to first render instead.
const ChatMessageList = lazy(() =>
  import('./features/chatbot/components/ChatMessageList').then((m) => ({ default: m.ChatMessageList })),
);
// Conversational API Agent (Agent V2) — hosted INSIDE the existing chatbot panel.
const AgentConversation = lazy(() => import('./features/agent/components/AgentConversation'));

/**
 * Merge the server's slash commands with the local AI generation ones into the
 * single list SlashCommandDropdown renders.
 *
 * Shapes differ — the dropdown reads `{name, description, slash_command}` while
 * the generation list uses `{command, label, description}` — so the local ones
 * are mapped across. Server commands take precedence on a name clash.
 *
 * @param {Array} serverCommands
 * @returns {Array} combined command list
 */
function mergeSlashCommands(serverCommands) {
  const list = Array.isArray(serverCommands) ? [...serverCommands] : [];
  const taken = new Set(
    list.map((c) => (c.slash_command || c.slashCommand || c.name || '').toLowerCase()),
  );
  for (const cmd of AI_GENERATION_COMMANDS) {
    if (taken.has(cmd.command.toLowerCase())) continue;
    list.push({
      name: cmd.label,
      description: cmd.description,
      slash_command: cmd.command,
    });
  }
  return list;
}

/**
 * AppContent component that handles authentication guard, navbar visibility, and route rendering
 * Requirements: 2.2, 3.1, 3.2, 3.3, 3.4, 5.1, 5.2, 5.3, 5.4, 6.1, 6.2
 */
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const activeRole = useSelector((state) => state.roles.activeRole);

  // ─── Chatbot state ─────────────────────────────────────────
  const preference = useAgentPreference();
  const chatbot = useChatbot();
  // Agent V2: the existing panel can switch its body to the smarter agent.
  // Agent is the PRIMARY and ONLY experience when Agent V2 is enabled.
  // No Chat/Agent toggle — the unified agent handles both questions and tasks.
  const agentV2Enabled = import.meta.env.VITE_AGENT_V2_ENABLED === 'true';
  const sessions = useChatSessions();
  const chatContext = useChatContext();
  const pinsBookmarks = usePinsAndBookmarks();
  const isSandbox = useSandboxMode();
  const [sessionSidebarOpen, setSessionSidebarOpen] = useState(false);
  const [slashCommands, setSlashCommands] = useState([]);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [parentSummaryText, setParentSummaryText] = useState(null);

  // ─── Project data for chatbot @mention (Orchestration page) ────────────────
  const globalCtx = useContext(GlobalContext);
  const { chatMentionRequests, chatMentionCollections, chatMentionFolders } = useMemo(() => {
    const reqs = [];
    const cols = [];
    const flds = [];
    const { projects, metaData } = globalCtx || {};
    if (!projects || !Array.isArray(projects)) return { chatMentionRequests: reqs, chatMentionCollections: cols, chatMentionFolders: flds };

    const activeProjectId = metaData?.active_project;
    const targetProjects = activeProjectId
      ? projects.filter(p => p.id === activeProjectId)
      : projects;

    for (const project of targetProjects) {
      if (project.collections) {
        for (const col of project.collections) {
          cols.push({ id: col.id, name: col.name || col.collection_name, requests: col.requests });
          if (col.requests) {
            for (const req of col.requests) {
              reqs.push(req);
            }
          }
          if (col.folders) {
            for (const folder of col.folders) {
              flds.push({ id: folder.id, name: folder.name || folder.folder_name, requests: folder.requests });
              if (folder.requests) {
                for (const req of folder.requests) {
                  reqs.push(req);
                }
              }
            }
          }
        }
      }
    }
    return { chatMentionRequests: reqs, chatMentionCollections: cols, chatMentionFolders: flds };
  }, [globalCtx]);

  // Orchestration chatbot routing — detects orchestration intent and routes
  // through the AI generation pipeline instead of regular chat flow.
  const orchestrationRouting = useOrchestrationChatbotRouting(chatContext);

  // Unified send handler: routes orchestration-intent messages through AI generation,
  // all other messages through the regular chatbot SSE flow.
  const handleChatSend = useCallback(
    (text, file, contextAttachments, mentions) => {
      if (orchestrationRouting.shouldRouteToOrchestration(text)) {
        orchestrationRouting.handleOrchestrationMessage(text, mentions);
      } else {
        // Include mentions in context for non-orchestration messages if present
        const ctx = mentions && mentions.length > 0 ? { ...chatContext, mentions } : chatContext;
        chatbot.send(text, ctx);
      }
    },
    [orchestrationRouting, chatbot, chatContext],
  );

  // CRITICAL: Check for landing page FIRST, before any auth logic
  const isLandingPage = location.pathname === '/';

  // Define public/auth routes that should NOT show navbar/sidebar
  const publicRoutes = [
    ROUTES.SIGN_IN,
    ROUTES.ORGANIZATION_SIGNUP,
    ROUTES.ACCEPT_INVITE,
    ROUTES.SIGN_UP,
  ];

  const isAuthPage = publicRoutes.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Authentication Guard Logic - Only runs for non-landing pages
  // Requirements: 3.1, 3.2, 3.3, 3.4
  useEffect(() => {
    // Skip auth check entirely for landing page
    if (isLandingPage) return;

    const token = readToken();

    // Redirect to sign-in if not authenticated and not on a public route
    if (!token && !isAuthPage) {
      navigate(ROUTES.SIGN_IN, { replace: true });
    }
  }, [location.pathname, navigate, isLandingPage, isAuthPage]);

  // Fetch user roles once when authenticated (not on every route change)
  useEffect(() => {
    const token = readToken();
    const userId = sessionStorage.getItem(STORAGE_KEYS.USERID);
    if (token && userId) dispatch(fetchUserRoles(userId));
  }, [dispatch]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Fetch chatbot sessions and slash commands when authenticated
  // Also re-fetch preference when navigating from auth page to dashboard (after login)
  const isAuthenticated = !isLandingPage && !isAuthPage && !!readToken();

  useEffect(() => {
    if (isAuthenticated) {
      preference.fetchPreference();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && preference.onboardingCompleted) {
      sessions.fetchSessions();
      // Server commands first, then the generation ones the server does not know
      // about. Deduplicated by name so a server-side /generate-test wins.
      getCommands()
        .then((serverCommands) => setSlashCommands(mergeSlashCommands(serverCommands)))
        .catch(() => setSlashCommands(mergeSlashCommands([])));
    }
  }, [isAuthenticated, preference.onboardingCompleted]);

  // Auto-switch to new session on rollover
  useEffect(() => {
    if (chatbot.rolloverInfo?.new_session_id) {
      // Preserve the summary text so the child session can display it
      setParentSummaryText(chatbot.rolloverInfo.summary_preview || null);
      sessions.fetchSessions();
      chatbot.switchSession(chatbot.rolloverInfo.new_session_id);
    }
  }, [chatbot.rolloverInfo]);

  // Refresh session list after every message stream completes (picks up smart titles + new sessions)
  useEffect(() => {
    if (isAuthenticated && preference.onboardingCompleted && !chatbot.isStreaming && chatbot.currentSessionId) {
      sessions.fetchSessions();
    }
  }, [chatbot.isStreaming]);

  // Render landing page immediately, no auth checks needed
  if (isLandingPage) {
    return <LandingPage />;
  }

  // Auth pages: render ONLY the route content, no Navbar/Sidebar wrapper
  if (isAuthPage) {
    return (
      <Routes>
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={route.element}
          />
        ))}
      </Routes>
    );
  }

  // Dashboard pages: full layout with Navbar + Sidebar + content
  const handleSidebarNavigate = (path) => {
    navigate(path);
    setMobileSidebarOpen(false);
  };

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-background">
      {/* Sandbox Welcome Modal */}
      <SandboxWelcomeModal />

      {/* Navbar - fixed height */}
      <div className="flex-none z-40">
        <Navbar onMobileMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} />
      </div>

      {/* Main content area: Sidebar + Page */}
      <div className="flex-1 flex flex-row overflow-hidden relative">
        {/* Mobile overlay backdrop */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <ImagoSidebar
          currentPath={location.pathname}
          onNavigate={handleSidebarNavigate}
          isOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          activeRole={activeRole}
        />

        {/* Route content */}
        <div className="flex-1 w-full h-full overflow-hidden relative">
          <Walkthrough />
          <Routes>
            {routes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
          </Routes>
        </div>
      </div>

      {/* ─── Chatbot UI ─────────────────────────────────────── */}

      {/* FAB + Panel — visible once authenticated and preference loaded, hidden for sandbox users */}
      {isAuthenticated && !preference.loading && !isSandbox && (
        <>
          {!chatbot.isOpen && (
            <ChatbotFAB
              agentType={preference.agentType}
              isOpen={chatbot.isOpen}
              onClick={chatbot.togglePanel}
            />
          )}

          <ChatbotPanel
            isOpen={chatbot.isOpen}
            agentType={preference.agentType}
            position={preference.position}
            agentOnly={agentV2Enabled}
            contextUsage={chatbot.contextUsage}
            onSwitchAgent={preference.switchAgent}
            onClose={chatbot.closePanel}
            showPersonaSelection={!preference.onboardingCompleted}
            onSelectPersona={preference.selectAgent}
            onNewChat={async () => {
              const id = await chatbot.startNewChat();
              if (id) sessions.fetchSessions();
            }}
            onToggleSessions={() => setSessionSidebarOpen((p) => !p)}
            onChangePosition={preference.changePosition}
            onClearChat={() => {
              if (chatbot.currentSessionId) {
                chatbot.loadMessages(chatbot.currentSessionId);
              }
            }}
            onExportChat={() => {
              const md = chatbot.messages
                .map((m) => `**${m.role === 'user' ? 'You' : preference.agentType === 'pro' ? 'Pro' : 'Guide'}:**\n${m.content}`)
                .join('\n\n---\n\n');
              const blob = new Blob([md], { type: 'text/markdown' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'chat-export.md';
              a.click();
              URL.revokeObjectURL(url);
            }}
            onExportPdf={async () => {
              try {
                // jspdf + html2canvas are ~706 kB and are only needed when the
                // user actually exports. Fetch them on the click rather than
                // paying for them on every page load.
                const { exportChatAsPdf } = await import('./features/chatbot/utils/pdfExport');
                await exportChatAsPdf(chatbot.messages, preference.agentType);
              } catch (err) {
                toast.error('Failed to export PDF');
              }
            }}
            searchQuery={chatSearchQuery}
            onSearchQueryChange={setChatSearchQuery}
          >
            <div className="flex flex-1 overflow-hidden">
              {/* Session sidebar */}
              <ChatSessionSidebar
                isOpen={sessionSidebarOpen}
                sessions={sessions.sessions}
                activeSessionId={chatbot.currentSessionId}
                onSelectSession={(id) => {
                  chatbot.switchSession(id);
                  setSessionSidebarOpen(false);
                  setParentSummaryText(null);
                }}
                onRenameSession={sessions.rename}
                onDeleteSession={async (id) => {
                  await sessions.remove(id);
                  if (id === chatbot.currentSessionId) {
                    chatbot.startNewChat();
                  }
                }}
                bookmarks={pinsBookmarks.bookmarks}
                onUnbookmark={pinsBookmarks.unbookmarkMessage}
                onGoToBookmark={(sessionId) => {
                  chatbot.switchSession(sessionId);
                  setSessionSidebarOpen(false);
                }}
              />

              {/* Messages + Input — Agent is the ONLY surface when V2 is enabled */}
              {/* One boundary for both surfaces: whichever renders, its chunk is
                  fetched on first open rather than at app start. */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {agentV2Enabled ? (
                  <Suspense fallback={<div className="flex-1 flex items-center justify-center text-xs text-muted">Loading…</div>}>
                    <AgentConversation persona={preference.agentType} />
                  </Suspense>
                ) : (
                <>
                <Suspense fallback={<div className="flex-1 flex items-center justify-center text-xs text-muted">Loading…</div>}>
                <ChatMessageList
                  messages={chatbot.messages}
                  isStreaming={chatbot.isStreaming}
                  agentType={preference.agentType}
                  onFeedback={(msgId, fb) => submitFeedback(msgId, fb).catch(() => {})}
                  onRegenerate={chatbot.retry}
                  streamError={chatbot.streamError}
                  onRetry={chatbot.retry}
                  contextUsage={chatbot.contextUsage}
                  rolloverInfo={chatbot.rolloverInfo}
                  thresholdWarningShown={chatbot.thresholdWarningShown}
                  parentSessionId={
                    sessions.sessions.find(
                      (s) => (s.session_id || s.sessionId) === chatbot.currentSessionId
                    )?.parent_session_id || null
                  }
                  parentSessionTitle={
                    (() => {
                      const current = sessions.sessions.find(
                        (s) => (s.session_id || s.sessionId) === chatbot.currentSessionId
                      );
                      if (!current?.parent_session_id) return null;
                      const parent = sessions.sessions.find(
                        (s) => (s.session_id || s.sessionId) === current.parent_session_id
                      );
                      return parent?.title || 'Previous session';
                    })()
                  }
                  onGoToSession={(id) => {
                    chatbot.switchSession(id);
                    sessions.fetchSessions();
                  }}
                  pinnedMessages={pinsBookmarks.getPins(chatbot.currentSessionId)}
                  onPinMessage={(msg) => pinsBookmarks.pinMessage(chatbot.currentSessionId, msg)}
                  onUnpinMessage={(msgId) => pinsBookmarks.unpinMessage(chatbot.currentSessionId, msgId)}
                  isPinned={(msgId) => pinsBookmarks.isPinned(chatbot.currentSessionId, msgId)}
                  onBookmarkMessage={(msg) => {
                    const currentSession = sessions.sessions.find(
                      (s) => (s.session_id || s.sessionId) === chatbot.currentSessionId
                    );
                    pinsBookmarks.bookmarkMessage(chatbot.currentSessionId, currentSession?.title, msg);
                  }}
                  onUnbookmarkMessage={pinsBookmarks.unbookmarkMessage}
                  isBookmarked={pinsBookmarks.isBookmarked}
                  searchQuery={chatSearchQuery}
                  parentSummaryText={parentSummaryText}
                  onEditMessage={(msgId, newText) => {
                    // Edit: truncate messages after the edited one, then resend
                    const idx = chatbot.messages.findIndex((m) => m.message_id === msgId);
                    if (idx >= 0) {
                      chatbot.editAndResend(msgId, newText, chatContext);
                    }
                  }}
                  followupSuggestions={chatbot.followupSuggestions}
                  onFollowupClick={(text) => handleChatSend(text)}
                />
                </Suspense>
                {/* Orchestration AI action preview — shown inline when routing through AI generation */}
                {orchestrationRouting.isOrchestrationRouted && (
                  <div className="px-3 py-2 border-t border-gray-700/40 shrink-0">
                    <OrchestrationChatPreview
                      actions={orchestrationRouting.orchestrationAI.previewActions}
                      warnings={orchestrationRouting.orchestrationAI.warnings}
                      error={orchestrationRouting.orchestrationAI.error}
                      isGenerating={orchestrationRouting.orchestrationAI.isGenerating}
                      isApplying={orchestrationRouting.orchestrationAI.isApplying}
                      onConfirm={orchestrationRouting.orchestrationAI.apply}
                      onCancel={orchestrationRouting.orchestrationAI.cancel}
                    />
                  </div>
                )}
                <ChatInput
                  onSend={handleChatSend}
                  isStreaming={chatbot.isStreaming || orchestrationRouting.orchestrationAI.isGenerating}
                  onUploadFile={uploadFile}
                  commands={slashCommands}
                  currentPage={chatContext.page}
                  projectRequests={chatMentionRequests}
                  collections={chatMentionCollections}
                  folders={chatMentionFolders}
                />
                </>
                )}
              </div>
            </div>
          </ChatbotPanel>
        </>
      )}
    </div>
  );
}

/**
 * Main App component with full provider hierarchy
 * Requirements: 1.1, 1.2, 1.3, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 */
function App() {
  return (
    // Redux Provider - Requirement 4.2
    <Provider store={store}>
      {/* GlobalProvider for legacy compatibility */}
      <GlobalProvider>
        {/* Environments slice, split out of GlobalProvider */}
        <EnvironmentsProvider>
        {/* Test-plans view state (results flow crosses routes, so app-level) */}
        <TestPlansProvider>
        {/* BrowserRouter - Requirement 4.1 */}
        <BrowserRouter>
          {/* ThemeProvider - Requirement 4.3 */}
          <ThemeProvider>
            {/* One poller for both notification bells - see the provider. */}
            <NotificationSummaryProvider>
            {/* GlobalLoader - Requirement 4.5 */}
            <GlobalLoader />

            {/* ToastContainer for notifications - Requirement 4.4 */}
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />

            {/* Main application content with authentication and routing */}
            <AppContent />
            </NotificationSummaryProvider>
          </ThemeProvider>
        </BrowserRouter>
        </TestPlansProvider>
        </EnvironmentsProvider>
      </GlobalProvider>
    </Provider>
  );
}

export default App;
